import { useState, type FormEvent } from 'react';
import { useTranslation } from 'react-i18next';
import { formatIqd, fromMillidinars, type DrawerCloseResultDto } from '@gc/shared';

import { ApiError } from '@/lib/api';
import {
  canRecount,
  closeDrawer,
  closeFlow,
  closeNoteRequired,
  parseWholeIqd,
  previewClose,
  START_CLOSE,
  toSafeMd,
  type CloseFlow,
  type CloseFlowEvent,
} from './useCashDrawer';

/**
 * The difference, worded "more / less than expected" in both directions. The
 * expected cash can be NEGATIVE (a payout-heavy session on a small float),
 * so neither direction is hardcoded as the bad one. The server computed it;
 * this only says it.
 */
export function DrawerVarianceLine({
  varianceMd,
  currencySymbol,
}: {
  varianceMd: string;
  currencySymbol: string;
}) {
  const { t } = useTranslation();
  const variance = BigInt(varianceMd);
  const amount = formatIqd(fromMillidinars(variance < 0n ? -variance : variance), {
    currencySymbol,
  });

  if (variance === 0n) {
    return (
      <p data-variance="0" className="mt-2 text-sm text-content-secondary">
        {t('cashDrawer.close.varianceZero')}
      </p>
    );
  }
  return (
    <p data-variance={varianceMd} className="mt-2 text-sm font-medium text-ending">
      {t(variance > 0n ? 'cashDrawer.close.varianceOver' : 'cashDrawer.close.varianceUnder', {
        amount,
      })}
    </p>
  );
}

/**
 * Count and close the venue's drawer — count FIRST (owner, 2026-10-12).
 *
 * 1. The count, typed into an empty field with nothing else on screen: no
 *    expected figure to agree with.
 * 2. The server answers the expected cash and the difference for that count,
 *    saving nothing. A difference may be counted once more.
 * 3. A difference needs a note.
 * 4. The float to keep (0 … counted); the rest goes to the safe.
 * 5. Confirm. The expected figure step 2 answered rides along with the
 *    session being closed, and the server refuses (`drawer_stale`) if cash
 *    moved since or that session is already closed — the close then starts
 *    again from an empty count.
 *
 * The next session opens on the server with the kept float, so the drawer is
 * never left closed.
 */
export function CloseDrawerDialog({
  sessionId,
  openingFloatMd,
  currencySymbol,
  onClose,
  onClosed,
  onStale,
}: {
  /** The OPEN session being closed (`DrawerCurrentDto.session.id`), sent with the close. */
  sessionId: string;
  /** The open session's float — said beside the kept-float field, never copied into it. */
  openingFloatMd: string;
  currencySymbol: string;
  onClose: () => void;
  onClosed: (result: DrawerCloseResultDto) => void;
  /** The drawer moved or stopped; the parent re-reads it. */
  onStale: () => void;
}) {
  const { t } = useTranslation();
  const [flow, setFlow] = useState<CloseFlow>(START_CLOSE);
  const [raw, setRaw] = useState('');
  const [note, setNote] = useState('');
  const [keptRaw, setKeptRaw] = useState('');
  const [error, setError] = useState<string | null>(null);
  const [busy, setBusy] = useState(false);
  const money = (value: string) => formatIqd(fromMillidinars(value), { currencySymbol });

  const countedIqd = parseWholeIqd(raw);
  const keptIqd = parseWholeIqd(keptRaw);
  const noteRequired = closeNoteRequired(flow);
  const toSafe =
    flow.step === 'review' && keptIqd !== null ? toSafeMd(flow.countedIqd, keptIqd) : null;
  const keptTooMuch = flow.step === 'review' && keptIqd !== null && toSafe === null;

  function step(event: CloseFlowEvent): void {
    setFlow((state) => closeFlow(state, event));
  }

  /** Everything typed for a count the drawer no longer matches. */
  function clearTyped(): void {
    setRaw('');
    setNote('');
    setKeptRaw('');
  }

  function refused(caught: unknown): void {
    const code = caught instanceof ApiError ? caught.code : null;
    if (code === 'drawer_stale') {
      // The count was made against cash that is no longer there. Cleared, so
      // the recount is of the drawer as it is — leaving it would be a prefill.
      step({ type: 'stale' });
      clearTyped();
      setError(t('cashDrawer.close.stale'));
      onStale();
    } else if (code === 'drawer_note_required') {
      setError(t('cashDrawer.close.noteNeeded'));
    } else if (code === 'drawer_kept_exceeds_counted') {
      setError(t('cashDrawer.close.keptExceeds'));
    } else if (code === 'drawer_not_enabled') {
      setError(t('cashDrawer.close.notEnabled'));
      onStale();
    } else {
      setError(caught instanceof ApiError ? caught.message : t('cashDrawer.close.failed'));
    }
  }

  async function check(): Promise<void> {
    if (countedIqd === null) return;
    setError(null);
    setBusy(true);
    try {
      const preview = await previewClose(countedIqd);
      step({ type: 'previewed', countedIqd, preview });
    } catch (caught) {
      refused(caught);
    } finally {
      setBusy(false);
    }
  }

  async function confirm(): Promise<void> {
    if (flow.step !== 'review' || keptIqd === null || toSafe === null) return;
    if (noteRequired && !note.trim()) return;
    setError(null);
    setBusy(true);
    try {
      onClosed(
        await closeDrawer(
          { countedCashIqd: flow.countedIqd, keptFloatIqd: keptIqd, note },
          flow.preview.expectedCashMd,
          sessionId,
        ),
      );
    } catch (caught) {
      refused(caught);
    } finally {
      setBusy(false);
    }
  }

  function handleSubmit(event: FormEvent): void {
    event.preventDefault();
    void (flow.step === 'count' ? check() : confirm());
  }

  function recount(): void {
    step({ type: 'recount' });
    clearTyped();
    setError(null);
  }

  const inputClass =
    'tabular w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary';
  const submitDisabled =
    flow.step === 'count'
      ? busy || countedIqd === null
      : busy || keptIqd === null || keptTooMuch || (noteRequired && !note.trim());

  return (
    <div className="fixed inset-0 z-50 grid place-items-center bg-black/60 p-4">
      <form
        onSubmit={handleSubmit}
        role="dialog"
        aria-modal="true"
        aria-label={t('cashDrawer.close.title')}
        className="max-h-full w-full max-w-lg overflow-auto rounded-card border border-subtle bg-ground p-5 shadow-2xl"
      >
        <h2 className="text-lg font-semibold text-content">{t('cashDrawer.close.title')}</h2>

        {flow.step === 'count' ? (
          <>
            <p className="mt-2 text-sm leading-relaxed text-content-secondary">
              {t(flow.recounted ? 'cashDrawer.close.recountIntro' : 'cashDrawer.close.countIntro')}
            </p>
            <label className="mt-4 block">
              <span className="mb-1 block text-sm text-content-secondary">
                {t('cashDrawer.close.countedLabel')}
              </span>
              <div className="flex items-center gap-2">
                <input
                  name="counted"
                  value={raw}
                  onChange={(event) => setRaw(event.target.value.replace(/\D/g, ''))}
                  inputMode="numeric"
                  autoComplete="off"
                  autoFocus
                  placeholder={t('cashDrawer.close.countedPlaceholder')}
                  className={inputClass}
                />
                <span className="text-sm text-content-muted">{currencySymbol}</span>
              </div>
              <span className="mt-1 block text-xs text-content-muted">
                {t('cashDrawer.close.countedHint')}
              </span>
            </label>
          </>
        ) : (
          <>
            <dl className="tabular mt-4 divide-y divide-subtle rounded-card border border-subtle bg-raised text-sm">
              <div
                data-line="counted"
                className="flex items-baseline justify-between gap-4 px-3 py-2"
              >
                <dt className="text-content-secondary">{t('cashDrawer.close.youCounted')}</dt>
                <dd className="text-content">
                  {money((BigInt(flow.countedIqd) * 1000n).toString())}
                </dd>
              </div>
              <div
                data-line="expected"
                className="flex items-baseline justify-between gap-4 px-3 py-2"
              >
                <dt className="font-medium text-content">{t('cashDrawer.close.expected')}</dt>
                <dd className="text-base font-semibold text-content">
                  {money(flow.preview.expectedCashMd)}
                </dd>
              </div>
            </dl>
            <DrawerVarianceLine
              varianceMd={flow.preview.varianceMd}
              currencySymbol={currencySymbol}
            />

            {canRecount(flow) ? (
              <div className="mt-2 flex flex-wrap items-center gap-2">
                <button
                  type="button"
                  onClick={recount}
                  className="rounded-full border border-subtle px-3 py-1.5 text-xs text-content-secondary hover:bg-hover"
                >
                  {t('cashDrawer.close.recount')}
                </button>
                <span className="text-xs text-content-muted">
                  {t('cashDrawer.close.recountHint')}
                </span>
              </div>
            ) : null}

            <label className="mt-3 block">
              <span className="mb-1 block text-sm text-content-secondary">
                {t(noteRequired ? 'cashDrawer.close.noteRequired' : 'cashDrawer.close.note')}
              </span>
              <input
                name="note"
                value={note}
                onChange={(event) => setNote(event.target.value)}
                maxLength={500}
                required={noteRequired}
                placeholder={t('cashDrawer.close.notePlaceholder')}
                className="w-full rounded-control border border-subtle bg-input px-3 py-2 text-content outline-none focus:border-primary"
              />
            </label>

            <label className="mt-3 block">
              <span className="mb-1 block text-sm text-content-secondary">
                {t('cashDrawer.close.keptLabel')}
              </span>
              <div className="flex items-center gap-2">
                <input
                  name="kept"
                  value={keptRaw}
                  onChange={(event) => setKeptRaw(event.target.value.replace(/\D/g, ''))}
                  inputMode="numeric"
                  autoComplete="off"
                  className={inputClass}
                />
                <span className="text-sm text-content-muted">{currencySymbol}</span>
              </div>
              <span className="mt-1 block text-xs text-content-muted">
                {t('cashDrawer.close.keptHint', { amount: money(openingFloatMd) })}
              </span>
            </label>

            {keptTooMuch ? (
              <p data-kept-too-much="" className="mt-2 text-sm font-medium text-danger">
                {t('cashDrawer.close.keptTooMuch')}
              </p>
            ) : toSafe !== null ? (
              <p data-to-safe={toSafe} className="tabular mt-2 text-sm text-content-secondary">
                {t('cashDrawer.close.toSafe', { amount: money(toSafe) })}
              </p>
            ) : null}
          </>
        )}

        {error ? (
          <p role="alert" className="mt-3 text-sm text-danger">
            {error}
          </p>
        ) : null}

        <div className="mt-5 flex justify-end gap-2">
          <button
            type="button"
            onClick={onClose}
            className="rounded-full border border-subtle px-4 py-2 text-sm text-content-secondary hover:bg-hover"
          >
            {t('common.cancel')}
          </button>
          <button
            type="submit"
            disabled={submitDisabled}
            className="rounded-full bg-primary px-4 py-2 text-sm font-medium text-primary-fg hover:bg-primary-hover disabled:opacity-60"
          >
            {busy
              ? t('common.loading')
              : t(flow.step === 'count' ? 'cashDrawer.close.check' : 'cashDrawer.close.submit')}
          </button>
        </div>
      </form>
    </div>
  );
}
